import React, { useEffect, useRef } from 'react';

interface InfiniteScrollSentinelProps {
  onVisible: () => void;
  active?: boolean;
  loading?: boolean;
  labelLoading?: string;
  labelEnd?: string;
  showEnd?: boolean;
}

export const InfiniteScrollSentinel: React.FC<InfiniteScrollSentinelProps> = ({
  onVisible,
  active = true,
  loading = false,
  labelLoading = 'Loading…',
  labelEnd = 'No more items',
  showEnd = false,
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const flagsRef = useRef({ active, loading, onVisible });

  // Keep latest flags and callback - avoids stale closure inside observer callback
  useEffect(() => {
    flagsRef.current = { active, loading, onVisible };
  }, [active, loading, onVisible]);

  useEffect(() => {
    if (!active) return;
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const { active: a, loading: l, onVisible: onVisibleCallback } = flagsRef.current;
            if (a && !l) onVisibleCallback();
          }
        });
      },
      {
        // Prefetch earlier to keep scrolling smooth
        root: null,
        rootMargin: '800px 0px',
        threshold: 0,
      },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [active]);

  return (
    <div ref={ref} className="px-6 py-4 border-t border-border text-center">
      {loading && <p className="text-sm text-muted-foreground">{labelLoading}</p>}
      {showEnd && !loading && <p className="text-sm text-muted-foreground">{labelEnd}</p>}
    </div>
  );
};
